/**
 * The Floor Plan step's identify card host (`floor-plan-page-identify`, R1–R20).
 *
 * The Checks tab's identify rows hand their group here (`onIdentifyGroup`); this owns which group the
 * card is open on, the close rules, moving to the next group, the group's lighting and the written
 * machines' arrival on the plan, and binds the shared card (`IdentifyCard` `layout="plan"`) and its
 * writes (`useIdentifyGroup`) through the Equipment step's own binding
 * (`useRegisterIdentifyBinding`), so both steps write the same way.
 *
 * A render prop, because the card docks beside the plan and the lighting goes into the planner — two
 * places in `OnboardingPlanner`:
 *
 *     <FloorPlanIdentify …>{({ dock, openKey, onIdentifyGroup, groupLit, arrived }) => …}</FloorPlanIdentify>
 *
 * Rendered only on the Floor Plan step, so its toasts (Undo) live as long as the step.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IdentifyCard,
  planIdentifyToastSub,
  useIdentifyGroup,
  type IdentifyCardProps,
} from '@repo/ui';
import {
  IDENTIFY_CARD_STATES,
  nextGroupToIdentify,
  opensIdentifyCard,
  projectedScore,
  type IdentifyGroup,
  type ManufacturerIdentifiedScore,
  type PlanChecks,
} from '@repo/front-end-utils';
import {
  findManufacturerCatalog,
  getIdentifyProductsByManufacturer,
  type IdentifyProduct,
} from '@repo/front-end-services';

import { unlessAborted, useRegisterIdentifyBinding } from './EquipmentIdentify';

/** The toast region over the plan pane (`OnboardingPlanner` mounts it). */
export const PLAN_TOAST_REGION = 'floor-plan';

/** The states the card opens on — the one list the Checks tab's rows also read. */
const offered = opensIdentifyCard;

/**
 * What one press hands its own write, so the toast it raises names that press's product and score
 * and not the last press's (a toast's *Try again* can land after another press; review: failure
 * paths, correctness). `useIdentifyGroup` passes the group object back to `scoreFor` and `toastSub`.
 */
interface PressGroup {
  label: string;
  product: IdentifyProduct | undefined;
  /**
   * Rows identified by earlier presses whose re-read has not landed — counted as identified. With
   * `base`, taken when the press starts, and again when a toast's *Try again* starts it once more.
   */
  pending: number;
  base: ManufacturerIdentifiedScore | null;
  /**
   * This press's rows `base` already shows identified. Empty for a first press (every row is offered);
   * on *Try again* after an answer was lost, the rows the lost call wrote, which its replayed answer
   * names again and must not be counted twice (review: Copilot).
   */
  shown: ReadonlySet<number>;
}

const EMPTY_GROUPS: readonly IdentifyGroup[] = [];

/** How the group reads in the toast — as its Checks row names it, *“LF 95T”*. */
const toastLabel = (group: IdentifyGroup) => `“${group.label}”`;

/** What the polite live region says when the card opens on a group (Block A). */
export const openedAnnouncement = (group: IdentifyGroup): string =>
  `Identify ${group.rowIds.length} ${group.rowIds.length === 1 ? 'machine' : 'machines'}, product name “${group.label}”`;

/**
 * The Floor Plan picker's catalogue reads — the manufacturer search and a maker's products — for the
 * identify card and the machine card's *Change Product* (`floor-plan-page-machine-card`), so both
 * read the catalogue the same way.
 */
export const usePlanIdentifyFetchers = (
  baseUrl: string,
  getToken: () => Promise<string>,
): NonNullable<IdentifyCardProps['planFetchers']> => {
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  return useMemo<NonNullable<IdentifyCardProps['planFetchers']>>(
    () => ({
      manufacturers: async (text, signal) => {
        const token = await getTokenRef.current();
        // Only the manufacturer facet is read, which the server works out over every match, so
        // one row is asked for (`manufacturer-products.service.ts`, the facet loop).
        const found = await unlessAborted(
          findManufacturerCatalog(baseUrl, token, { q: text, limit: 1 }),
          signal,
        );
        return found.facets.manufacturer;
      },
      products: async (maker, signal) => {
        const token = await getTokenRef.current();
        return unlessAborted(getIdentifyProductsByManufacturer(baseUrl, token, maker), signal);
      },
    }),
    [baseUrl],
  );
};

export interface FloorPlanIdentifyRender {
  /** The card, or null when it is closed — for the plan pane's panel slot. */
  dock: React.ReactNode;
  /** The group the card is open on, for its Checks row's current look. */
  openKey: string | null;
  /** The Checks tab's identify rows call this (`equipmentPanel.onIdentifyGroup`). */
  onIdentifyGroup: (group: IdentifyGroup) => void;
  /** The open group's placed machines, lit on the plan (R12). */
  groupLit: readonly string[] | null;
  /** The machines the last write identified, once the re-read has landed (R13). */
  arrived: ReadonlySet<string> | null;
  /** The live region announcing the group the card opened on. */
  status: React.ReactNode;
}

export interface FloorPlanIdentifyProps {
  /** Null while no twin is open: nothing is offered. */
  twinId: number | null;
  /** The planner's Checks model; null while there is none. */
  planChecks: PlanChecks | null;
  /** The Checks tab is the sidebar's tab: the card belongs to it (R4). */
  active: boolean;
  /**
   * True closes the card — a machine was selected, and its card takes the slot
   * (`floor-plan-page-machine-card`, R4). It does not stop the card opening again: a Checks identify
   * row clears the selection and opens its group in the same press.
   */
  closedBy?: boolean;
  baseUrl: string;
  getToken: () => Promise<string>;
  currentTwinId: () => number | null;
  reloadRegister: () => void;
  beginRegisterWrite: () => number | null;
  settleRegisterWrite: (
    token: number | null,
    outcome: { ok: boolean; reason?: string | null },
  ) => void;
  children: (render: FloorPlanIdentifyRender) => React.ReactNode;
}

export const FloorPlanIdentify: React.FC<FloorPlanIdentifyProps> = ({
  twinId,
  planChecks,
  active,
  closedBy = false,
  baseUrl,
  getToken,
  currentTwinId,
  reloadRegister,
  beginRegisterWrite,
  settleRegisterWrite,
  children,
}) => {
  /*
   * The model as last read with its register. A write's own re-read marks the register loading for a
   * moment (`registerReadState`), and the model then carries no groups; read as "nothing left", that
   * closed the card under the operator instead of moving on (found in the real-app A/B). So the last
   * read model stands until the register is read again — for the same twin only.
   */
  const known = useRef<{ twinId: number | null; checks: PlanChecks } | null>(null);
  if (planChecks?.registerKnown) known.current = { twinId, checks: planChecks };
  else if (known.current && known.current.twinId !== twinId) known.current = null;
  const knownChecks = known.current?.checks ?? null;
  const groups = knownChecks ? knownChecks.identify : EMPTY_GROUPS;
  /** Each register row's identify state, as the groups carry it; a row in none is identified. */
  const stateOf = useMemo(() => {
    const states = new Map<number, IdentifyGroup['state']>();
    groups.forEach(group => group.rowIds.forEach(id => states.set(id, group.state)));
    return (id: number) => states.get(id);
  }, [groups]);

  const [open, setOpen] = useState<{ key: string; index: number } | null>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  const stateOfRef = useRef(stateOf);
  stateOfRef.current = stateOf;
  const checksRef = useRef(knownChecks);
  checksRef.current = knownChecks;
  /** The Checks row that opened the card, for focus when it closes (R3). */
  const opener = useRef<HTMLElement | null>(null);
  const [said, setSaid] = useState({ n: 0, text: '' });

  // A new twin starts closed.
  useEffect(() => setOpen(null), [twinId]);
  // A machine selected: its card takes the slot, and this one closes rather than waiting behind it.
  useEffect(() => {
    if (closedBy) setOpen(null);
  }, [closedBy]);

  /*
   * Groups a write has just landed on, with the rows it wrote, until the register re-reads them —
   * `EquipmentIdentify`'s rule: moving on skips them, and a group is released once any row written
   * has left the offered states (the re-read carrying the write has landed).
   */
  const settling = useRef(new Map<string, readonly number[]>());
  /**
   * Written rows waiting for the re-read before their machine glows, each with that machine (null
   * when it is not placed). Per row, not per group: a row glows once the re-read shows IT identified,
   * so a large group's failed chunk leaves its rows dark and waiting for *Try again*, and a second
   * press before the first's re-read keeps the first's rows (review: correctness, Copilot).
   */
  const pendingArrival = useRef(new Map<number, string | null>());
  /** The rows glowing, with their machine, so an Undo dims only the rows it restored. */
  const [arrivedRows, setArrivedRows] = useState<ReadonlyMap<number, string> | null>(null);
  useEffect(() => {
    settling.current.forEach((rows, key) => {
      if (rows.some(id => !offered(stateOf(id)))) settling.current.delete(key);
    });
    const landed = new Map<number, string>();
    pendingArrival.current.forEach((refid, row) => {
      if (offered(stateOf(row))) return;
      pendingArrival.current.delete(row);
      if (refid !== null) landed.set(row, refid);
    });
    if (landed.size > 0) {
      setArrivedRows(prev => {
        // forEach, not a spread: Maps under an ES5 target.
        const next = new Map<number, string>(landed);
        prev?.forEach((refid, row) => next.set(row, refid));
        return next;
      });
    }
  }, [stateOf]);
  const arrived = useMemo(() => {
    if (!arrivedRows || arrivedRows.size === 0) return null;
    const refids = new Set<string>();
    arrivedRows.forEach(refid => refids.add(refid));
    return refids;
  }, [arrivedRows]);

  const openGroup = open ? (groups.find(group => group.key === open.key) ?? null) : null;

  /*
   * Where focus goes when the card closes (R3): the Checks row of the group it was open on, else the
   * row that opened it, else the identify group's header, else the Checks panel. The row of a group
   * just identified has left the list; the identify group's BODY carries the `plan-checks-identify`
   * id but cannot take focus, so its header (`aria-controls`) is the heading meant (review: frontend,
   * spec compliance).
   */
  const focusAfterClose = useCallback((groupKey: string | null) => {
    window.setTimeout(() => {
      // Matched by attribute value, not a selector: a group's key is whatever the customer wrote.
      const ownRow = groupKey
        ? (Array.from(document.querySelectorAll<HTMLElement>('[data-refid]'))
            .find(el => el.getAttribute('data-refid') === `identify:${groupKey}`)
            ?.querySelector<HTMLElement>('button.row-select') ?? null)
        : null;
      const candidates = [
        ownRow,
        opener.current,
        document.querySelector<HTMLElement>('button[aria-controls="plan-checks-identify"]'),
        document.querySelector<HTMLElement>('[data-plan-checks]'),
      ];
      candidates.find(el => el?.isConnected)?.focus();
    }, 0);
  }, []);

  const close = useCallback(() => {
    const key = openRef.current?.key ?? null;
    setOpen(null);
    focusAfterClose(key);
  }, [focusAfterClose]);

  const openOn = useCallback((group: IdentifyGroup) => {
    const index = groupsRef.current.findIndex(g => g.key === group.key);
    setOpen({ key: group.key, index: Math.max(0, index) });
    setSaid(prev => ({ n: prev.n + 1, text: openedAnnouncement(group) }));
  }, []);

  /** Moving on (R17): the next group to identify or confirm after this one, wrapping; none closes. */
  const moveOn = useCallback(
    (from: { key: string; index: number }, afterWrite: boolean) => {
      const all = groupsRef.current;
      const list = all.filter(g => g.key === from.key || !settling.current.has(g.key));
      const at = list.findIndex(g => g.key === from.key);
      const index = at >= 0 ? at : Math.min(from.index, all.length);
      const next = nextGroupToIdentify(
        afterWrite ? list.filter(g => g.key !== from.key) : list,
        { key: from.key, index },
        IDENTIFY_CARD_STATES,
      );
      if (!next) {
        setOpen(null);
        focusAfterClose(null);
        return;
      }
      openOn(next);
    },
    [openOn, focusAfterClose],
  );
  const moveOnRef = useRef(moveOn);
  moveOnRef.current = moveOn;
  const movedFrom = useCallback(
    (from: { key: string; index: number }, written: readonly number[]) => {
      // Recorded whichever card is open: a press's rows count for later presses' scores, and its
      // group is skipped, until the re-read shows them — even if the operator opened another group
      // while it wrote (review: Copilot). Only the card's moving on waits for its own group.
      if (written.length > 0) settling.current.set(from.key, written);
      if (openRef.current?.key === from.key) moveOn(from, true);
    },
    [moveOn],
  );

  /*
   * Close rules (R3–R4): the card belongs to the Checks tab with a register to identify against.
   * A group settled some other way — a toast's Try again, another editor — has left the offered
   * states: move on as a write of the card's own would.
   */
  const available = active && twinId !== null && knownChecks !== null;
  useEffect(() => {
    if (!open) return;
    if (!available) setOpen(null);
    else if (!openGroup || !offered(openGroup.state)) moveOnRef.current(open, true);
  }, [open, available, openGroup]);

  const { api, fetchers } = useRegisterIdentifyBinding({
    twinId,
    baseUrl,
    getToken,
    currentTwinId,
    reloadRegister,
    beginRegisterWrite,
    settleRegisterWrite,
    settling,
  });
  const planApi = useMemo(
    () => ({
      ...api,
      undo: async (...args: Parameters<typeof api.undo>) => {
        const answer = await api.undo(...args);
        // Restored machines are to identify again, and a later identify of them glows anew. Only
        // this Undo's rows: another group's glow, or its wait for a re-read, stands (review: Copilot).
        const restored = new Set<number>(answer.alreadyRestored);
        answer.restored.forEach(row => restored.add(row.id));
        restored.forEach(row => pendingArrival.current.delete(row));
        setArrivedRows(prev => {
          if (!prev) return prev;
          const next = new Map<number, string>();
          prev.forEach((refid, row) => {
            if (!restored.has(row)) next.set(row, refid);
          });
          return next;
        });
        return answer;
      },
      toastRegion: PLAN_TOAST_REGION,
      // The press's own score: before, its base with every earlier write still awaiting its re-read
      // counted in; after, those and this write's rows.
      scoreFor: (group: { label: string }, _written: number, writtenIds: readonly number[]) => {
        const press = group as PressGroup;
        if (!press.base) return null;
        const counted = writtenIds.filter(id => !press.shown.has(id)).length;
        return {
          before: projectedScore(press.base, press.pending).after,
          after: projectedScore(press.base, press.pending + counted).after,
        };
      },
      toastSub: (group: { label: string }) => planIdentifyToastSub((group as PressGroup).product),
    }),
    [api],
  );
  const writes = useIdentifyGroup(planApi);

  const planFetchers = usePlanIdentifyFetchers(baseUrl, getToken);

  /**
   * A press's score baseline: the model's score, and the rows earlier presses wrote that it does not
   * show identified yet.
   */
  const scoreBaseline = (
    rows: readonly number[] = [],
  ): Pick<PressGroup, 'pending' | 'base' | 'shown'> => {
    let pending = 0;
    settling.current.forEach(written =>
      written.forEach(id => {
        if (offered(stateOfRef.current(id))) pending += 1;
      }),
    );
    const shown = new Set<number>();
    rows.forEach(id => {
      if (!offered(stateOfRef.current(id))) shown.add(id);
    });
    return { pending, base: checksRef.current?.score ?? null, shown };
  };

  const onIdentifyGroup = useCallback(
    (group: IdentifyGroup) => {
      if (!available || !offered(group.state)) return;
      const active = document.activeElement;
      opener.current = active instanceof HTMLElement ? active : null;
      openOn(group);
    },
    [available, openOn],
  );

  let dock: React.ReactNode = null;
  if (open && openGroup && available && offered(openGroup.state)) {
    const from = open;
    const rowIds = openGroup.rowIds;
    dock = (
      <IdentifyCard
        // Per group: every group starts on its own guess, with focus in its Product search.
        key={openGroup.key}
        layout="plan"
        placement="inset"
        headingLevel={3}
        group={{
          key: openGroup.key,
          label: openGroup.label,
          count: rowIds.length,
          sampleAssetId: rowIds[0],
          written: {
            category: openGroup.written.category,
            manufacturer: openGroup.written.manufacturer,
          },
          proposal: openGroup.proposal
            ? {
                manufacturer: openGroup.proposal.manufacturer,
                reference_id: openGroup.proposal.reference_id,
                reason: openGroup.proposal.reason,
              }
            : null,
        }}
        position={{ n: 1, of: 1 }}
        fetchers={fetchers}
        planFetchers={planFetchers}
        logContext={twinId !== null ? { twinId } : undefined}
        busy={writes.busy}
        onIdentify={(referenceId, _answer, product) => {
          /*
           * The machines that glow once the re-read shows the write (R13), taken BEFORE the write:
           * the group's key and its placed machines are the model's as it stands now, and the re-read
           * may land before this press's promise does. A press that fails leaves its rows offered,
           * so nothing glows until a *Try again* identifies them.
           */
          rowIds.forEach(row =>
            pendingArrival.current.set(row, checksRef.current?.refidOfRow(row) ?? null),
          );
          const press: PressGroup = {
            label: toastLabel(openGroup),
            product,
            ...scoreBaseline(),
          };
          void writes
            .identify(
              press,
              referenceId,
              rowIds,
              failed => {
                // *Try again* is a later press: the score has moved with whatever landed since.
                Object.assign(press, scoreBaseline(failed));
                return failed.filter(row => offered(stateOfRef.current(row)));
              },
              () => movedFrom(from, rowIds),
            )
            .then(outcome => {
              if (outcome === 'moved') movedFrom(from, rowIds);
            });
        }}
        onMarkNotTracked={() => undefined}
        onSkip={() => moveOn(from, false)}
        onClose={close}
      />
    );
  }

  const openKey = dock ? (open?.key ?? null) : null;
  const groupLit = useMemo(
    () => (openKey && knownChecks ? knownChecks.refidsOfGroup(openKey) : null),
    [openKey, knownChecks],
  );

  const status = (
    <div role="status" aria-live="polite" className="sr-only">
      <span key={said.n}>{dock ? said.text : ''}</span>
    </div>
  );

  return <>{children({ dock, openKey, onIdentifyGroup, groupLit, arrived, status })}</>;
};

export default FloorPlanIdentify;
